import { _t, getUiLang } from "app/i18n/i18n";
import { expandSectors } from "components/Subscriptions/SubscriptionsModal/SubscriptionsModal.helpers";
import { getAssetClass } from "../../../pages/redesign/publications/api/searchRequest/urlHelpers";
import { notAssetClass } from "../../../pages/redesign/publications/api/searchRequest/useGetFilters";

function getSecurityType(company) {
  return company?.slice(-2);
}

export function getTagsArray(publicationFieldObj) {
  const [[key, array]] = Object.entries(publicationFieldObj);

  return (
    array?.reduce((acc, item) => {
      const defaultItem = {
        key,
        tag: item.name?.[getUiLang()],
        selected: true,
      };

      switch (key) {
        case "analysts":
          if (item.isPrimary) {
            acc.push({
              ...defaultItem,
            });
          }
          break;

        case "primaryPeriodicals":
          acc.push({
            ...defaultItem,
            tag: item[getUiLang()],
          });
          break;

        case "companies":
          acc.push({
            ...defaultItem,
            securityType: getSecurityType(item.securityType),
          });
          break;

        default:
          acc.push({ ...defaultItem, tag: item[getUiLang()] });
      }

      return acc;
    }, []) || []
  );
}

export function hasField(array, key) {
  return array?.some((el) => el[getUiLang()] === _t(key, key));
}

export function hasAnyOfFields(array, keys) {
  return array?.some((el) => keys.map((key) => _t(key, key)).includes(el[getUiLang()]));
}

export function filterTagsBy(tags, filterKey, filterValue) {
  return tags.filter((tag) => tag[filterKey] === filterValue);
}

export function isEmpty(array) {
  return !array?.length || array === "undefined";
}

const qsKeyMap = {
  // Periodicals
  "Report Titles": { category: "Periodicals", key: "reportTitles" },
  "Report Titles-JA": { category: "Periodicals", key: "reportTitles" },
  reportTitles: { category: "Periodicals", key: "reportTitles" },
  "reportTitles.en": { category: "Periodicals", key: "reportTitles" },
  "reportTitles.ja": { category: "Periodicals", key: "reportTitles" },
  "periodicals.en": { category: "Periodicals", key: "periodicals" },
  "periodicals.ja": { category: "Periodicals", key: "periodicals" },
  // Report Types
  "reportTypes.en": { category: "Report Types", key: "reportTypes" },
  "reportTypes.ja": { category: "Report Types", key: "reportTypes" },
  // Analysts
  Analyst: { category: "Analysts", key: "analysts" },
  "Analyst-JA": { category: "Analysts", key: "analysts" },
  analysts: { category: "Analysts", key: "analysts" },
  "analysts.name.en": { category: "Analysts", key: "analysts" },
  "analysts.name.ja": { category: "Analysts", key: "analysts" },
  // Countries
  Country: { category: "Countries", key: "countries" },
  "Country-JA": { category: "Countries", key: "countries" },
  "countries.en": { category: "Countries", key: "countries" },
  "countries.ja": { category: "Countries", key: "countries" },
  // Regions
  Region: { category: "Regions", key: "regions" },
  "regions.en": { category: "Regions", key: "regions" },
  "regions.ja": { category: "Regions", key: "regions" },
  regions: { category: "Regions", key: "regions" },
  Regions: { category: "Regions", key: "regions" },
  "Region-JA": { category: "Regions", key: "regions" },
  // Sector
  sectors: { category: "Sectors", key: "gicsSectors" },
  "gicsSectors.en": { category: "Sectors", key: "gicsSectors" },
  "gicsSectors.ja": { category: "Sectors", key: "gicsSectors" },
  "gicsIndustryGroups.en": { category: "Sectors", key: "gicsIndustryGroups" },
  "gicsIndustryGroups.ja": { category: "Sectors", key: "gicsIndustryGroups" },
  // CompanyWithTicker / CompanySecurityType
  CompanyWithTicker: { category: "Companies", key: "companyWithTicker" },
  "CompanyWithTicker-JA": { category: "Companies", key: "companyWithTicker" },
  "companies.nameWithTicker.en": { category: "Companies", key: "companyWithTicker" },
  "companies.nameWithTicker.ja": { category: "Companies", key: "companyWithTicker" },
  // "companies.securityType": { category: "Companies", key: "companySecurityType" },
  // TxSubCategories
  "txSubCategories.en": { category: "Categories", key: "txSubCategories" },
  "txSubCategories.ja": { category: "Categories", key: "txSubCategories" },
  // Companies
  companies: { category: "Companies", key: "companies" },
  "companies.name.en": { category: "Companies", key: "companies" },
  "companies.name.ja": { category: "Companies", key: "companies" },
  // Asset Classes
  "Asset Class": { category: "Asset Classes", key: "assetClasses" },
  "Asset Class-JA": { category: "Asset Classes", key: "assetClasses" },
  assetClasses: { category: "Asset Classes", key: "assetClasses" },
  // Currency
  Currency: { category: "Currency", key: "currencies" },
  "Currency-JA": { category: "Currency", key: "currencies" },
  "currencies.en": { category: "Currency", key: "currencies" },
  "currencies.ja": { category: "Currency", key: "currencies" },
  // Theme
  Theme: { category: "Theme", key: "themes" },
  "Theme-JA": { category: "Theme", key: "themes" },
  "themes.en": { category: "Theme", key: "themes" },
  "themes.ja": { category: "Theme", key: "themes" },
  // Trade Idea
  "Trade Idea": { category: "Trade Idea", key: "tradeIdeas" },
  "Trade Idea-JA": { category: "Trade Idea", key: "tradeIdeas" },
  tradeIdeas: { category: "Trade Idea", key: "tradeIdeas" },
  "tradeIdeas.en": { category: "Trade Idea", key: "tradeIdeas" },
  "tradeIdeas.ja": { category: "Trade Idea", key: "tradeIdeas" },
};

function getAssetClassFromPath() {
  const category = getAssetClass();
  if (notAssetClass.includes(category)) return null;

  return category || null;
}

const unsupportedQsKeyToLabel = {
  Date: "Date",
  "contentType.en": "Format",
  "contentType.ja": "Format",
};

export function getUnsupportedFilterNames(qsObj) {
  const labels = new Set();
  Object.keys(qsObj).forEach((key) => {
    const label = unsupportedQsKeyToLabel[key];
    if (label) labels.add(label);
  });
  return Array.from(labels);
}

export function buildUnsupportedFiltersMessage(filterNames) {
  if (!filterNames.length) return null;

  const translated = filterNames.map((name) => _t(`unsupported-filter-${name}`));

  let filtersText;
  if (translated.length === 1) {
    filtersText = translated[0];
  } else {
    const last = translated[translated.length - 1];
    const rest = translated.slice(0, -1).join(", ");
    filtersText = `${rest} ${_t("unsupported-filters-and")} ${last}`;
  }

  const suffix =
    filterNames.length === 1 ? _t("unsupported-filters-suffix-singular") : _t("unsupported-filters-suffix-plural");

  return `${_t("unsupported-filters-prefix")}${filtersText}${suffix}`;
}

export function toCategoriesAndTags(qsObj, sectorData) {
  const byCategory = new Map();

  const assetClass = getAssetClassFromPath();
  if (assetClass) {
    byCategory.set("Asset Classes", [{ key: "txCategories", tag: assetClass, selected: true }]);
  }

  Object.entries(qsObj).forEach(([rawKey, rawVal]) => {
    const map = qsKeyMap[rawKey];
    if (!map) return;

    const langSuffix = new RegExp(/\.(en|ja)$/).exec(rawKey)?.[1] || null;

    const values = Array.isArray(rawVal) ? rawVal : [rawVal];
    const items = values
      .filter((v) => v != null && `${v}`.trim().length > 0)
      .map((tag) => {
        const item = { key: map.key, tag, selected: true };
        if (langSuffix) item.lang = langSuffix;
        return item;
      });

    if (!items.length) return;

    const existing = byCategory.get(map.category) || [];
    byCategory.set(map.category, existing.concat(items));
  });

  if (sectorData && byCategory.has("Sectors")) {
    const sectorTags = byCategory.get("Sectors");
    const expandedTags = expandSectors(sectorTags, sectorData) || sectorTags;
    byCategory.set("Sectors", expandedTags);
  }

  return Array.from(byCategory.entries());
}
